import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Alert, Button, Container, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { captureException, getSessionURL } from '@/utils/logrocket';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  static displayName = 'ErrorBoundary';
  
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
    
    captureException(error, {
      componentStack: errorInfo.componentStack,
      sessionURL: getSessionURL(),
    });
    
    this.setState({ error, errorInfo });
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      return (
        <Container size="sm" mt="xl">
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Something went wrong"
            color="red"
            variant="light"
          >
            <Stack gap="md">
              <Text>
                An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
              </Text>
              
              {import.meta.env.DEV && this.state.error && (
                <Stack gap="xs">
                  <Title order={6}>Error Details:</Title>
                  <Text size="sm" c="dimmed">
                    {this.state.error.toString()}
                  </Text>
                  {this.state.errorInfo && (
                    <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
                      {this.state.errorInfo.componentStack}
                    </Text>
                  )}
                </Stack>
              )}
              
              <Button onClick={this.handleReset} variant="light">
                Refresh Page
              </Button>
            </Stack>
          </Alert>
        </Container>
      );
    }

    return this.props.children;
  }
}